/**
 * 일자 표시 표준 (lab · partner 공통)
 *
 * 화면에 보이는 모든 날짜는 **YYYY-MM-DD**, 시각을 함께 쓸 때는 **YYYY-MM-DD HH:mm(:ss)** 다.
 * `toLocaleDateString('ko-KR')` 은 `2026. 9. 1.` 처럼 자릿수가 들쭉날쭉해 열 너비가 흔들리고
 * 정렬·복사·검색이 안 되므로 화면에서 쓰지 않는다(로케일에 따라 결과가 달라지기도 한다).
 *
 * 정본은 `frontend/src/utils/date.ts` 이며 이 파일은 동일 사본이다 — 한쪽만 고치지 말 것.
 */

export const DATE_FORMAT = 'YYYY-MM-DD';           // DatePicker format 프롭에 그대로 사용
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';
export const DATETIME_SEC_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const p2 = (n: number) => String(n).padStart(2, '0');

type DateLike = string | number | Date | null | undefined;

const toDate = (v: DateLike): Date | null => {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

/** 이미 YYYY-MM-DD 인 문자열은 Date 로 돌리지 않는다 (시간대 때문에 하루 밀리는 것을 막는다) */
const plainDate = (v: DateLike): string | null =>
  typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim()) ? v.trim() : null;

/** 2026-09-01 */
export const fmtDate = (v: DateLike, fallback = '-'): string => {
  const plain = plainDate(v);
  if (plain) return plain;
  const d = toDate(v);
  if (!d) return typeof v === 'string' && v.trim() ? v : fallback;
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
};

/** 2026-09-01 15:53 */
export const fmtDateTime = (v: DateLike, fallback = '-'): string => {
  const d = toDate(v);
  if (!d) return plainDate(v) ?? (typeof v === 'string' && v.trim() ? v : fallback);
  return `${fmtDate(d)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

/** 2026-09-01 15:53:07 — 초까지 필요한 로그성 화면에서만 */
export const fmtDateTimeSec = (v: DateLike, fallback = '-'): string => {
  const d = toDate(v);
  if (!d) return plainDate(v) ?? (typeof v === 'string' && v.trim() ? v : fallback);
  return `${fmtDate(d)} ${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
};

/** 2026-09-01 (월) — 요일이 의미를 갖는 화면(업무/출장 등)에서만 */
export const fmtDateWeekday = (v: DateLike, fallback = '-'): string => {
  const d = toDate(v);
  if (!d) return fallback;
  return `${fmtDate(d)} (${WEEKDAYS[d.getDay()]})`;
};

/** 2026-09 — 월 단위 표기 ('2026년 9월' 금지) */
export const fmtMonth = (v: DateLike, fallback = '-'): string => {
  const d = toDate(v);
  if (!d) return fallback;
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}`;
};

/** 2026-09-01 ~ 2026-09-07 (같은 날이면 하나만) */
export const fmtDateRange = (from: DateLike, to: DateLike, fallback = '-'): string => {
  const a = fmtDate(from, '');
  const b = fmtDate(to, '');
  if (!a && !b) return fallback;
  if (!b || a === b) return a || fallback;
  if (!a) return b;
  return `${a} ~ ${b}`;
};

/** 15:53:07 — 날짜 없이 시각만 (로그인 화면 시계 등) */
export const fmtTime = (v: DateLike, fallback = '-'): string => {
  const d = toDate(v);
  if (!d) return fallback;
  return `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
};
